import {useAuth} from '@dofleini/security';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import PropTypes from 'prop-types';
import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';

import useCrud from '@/hooks/useCrud';
import {useIsAdminByRoles} from '@/modules/admin/hooks/useUser';
import {CACHE_KEY_LIST_PROCEDURE, CACHE_KEY_PROCEDURE} from '@/modules/company/contants/procedure';
import ProcedureApiService from '@/modules/company/services/ProcedureApiService';
import {limpiarObjeto, updateOrganizationGroupBasedOnPartners} from '@/modules/company/utils';
import {transformKeyToObject} from '@/utils/utils';

const ProcedureContext = createContext();

function ProcedureProvider({children, proposal, company, data, companyId}) {
  const [procedure, setProcedure] = useState(data);
  const [processesInvolved, setProcessesInvolved] = useState(data?.processesInvolved || []);
  const [isDirectUpdate, setIsDirectUpdate] = useState(false);
  const [isMepFixUpdate, setIsMepFixUpdate] = useState(false);
  const {
    create,
    update,
    isCreating,
    isUpdating,
    isLoading
  } = useCrud(ProcedureApiService, CACHE_KEY_LIST_PROCEDURE, CACHE_KEY_PROCEDURE);
  const [step, setStep] = useState(isEmpty(data?.processesInvolved) ? 0 : 1);
  const {push, replace} = useHistory();
  const {reload} = useAuth();
  const isAdmin = useIsAdminByRoles();
  
  useEffect(() => {
    const element = cloneDeep(data);
    setProcedure(element);
    setProcessesInvolved(element?.processesInvolved || []);
    setIsDirectUpdate(element?.isDirectUpdate || false);
    setIsMepFixUpdate(element?.isMepFixUpdate || false);
  }, [data]);
  
  useEffect(() => {
    setStep(isEmpty(data?.processesInvolved) ? 0 : 1);
  }, [data?.processesInvolved]);
  
  const performSubmit = useCallback(async (payload, process, status, callback) => {
    let toSave = {
      company: companyId,
      processesInvolved: processesInvolved,
      subjectType: company?.subjectType,
      isDirectUpdate,
      isMepFixUpdate,
      expedient: company?.expedient,
      proposal: company?.proposal
    };
    if (process)
      toSave.dataByProcess = limpiarObjeto({...procedure?.dataByProcess, ...{[process]: payload}});
    else {
      toSave = {...toSave, ...payload};
    }
    // to be sure we are saving or updating
    if (procedure?.id)
      toSave._id = procedure?.id;
    // to be sure we are saving or updating
    if (status) {
      toSave.status = status;
    }
    // get the correct service
    const service = toSave._id ? update : create;
    
    return new Promise((resolve, reject) => {
      service(
        updateOrganizationGroupBasedOnPartners(toSave, {...company, ...transformKeyToObject(data?.dataByProcess, data?.processesInvolved)},
          processesInvolved
        )).then(({data}) => {
        // set the id to continue editing on the form
        callback && callback(data);
        resolve(data);
      })
        .catch(e => reject(e));
    });
  }, [company, companyId, create, data?.dataByProcess, data?.processesInvolved, isDirectUpdate, isMepFixUpdate, procedure?.dataByProcess, procedure?.id, processesInvolved, update]);
  
  const onSave = useCallback((payload, process, status = null) => {
    return performSubmit(payload, process, status, (data) => setProcedure(data));
  }, [performSubmit]);
  
  const onSaveAndRedirect = useCallback((payload, process) => {
    return performSubmit(payload, process, null, (data) => {
      setProcedure(data);
      replace(`/company/${data?.company?._id || data?.company}/procedure/${data?._id}/edit`);
    });
  }, [performSubmit, replace]);
  
  const onSubmit = useCallback(async (payload, process, status = null) => {
    return performSubmit(payload, process, status, (data) => {
      setProcedure(data);
      if (isAdmin) {
        push(`/company/procedures/${data?.id}`);
      } else {
        push(`/proposal/${proposal?.id}/procedures`);
      }
      reload();
    });
  }, [isAdmin, performSubmit, proposal?.id, push, reload]);
  
  const nextStep = useCallback(() => {
    setStep(prev => prev + 1);
  }, []);
  
  const prevStep = useCallback(() => {
    setStep(prev => prev - 1);
  }, []);
  
  const changeProcedureAndNext = useCallback(async (payload, process = null, skipSave = false) => {
    if (skipSave) {
      setProcessesInvolved(payload.processesInvolved);
      setIsDirectUpdate(payload.isDirectUpdate || false);
      setIsMepFixUpdate(payload.isMepFixUpdate || false);
    }
    if (payload.isDirectUpdate)
      await onSaveAndRedirect(pick(payload, ['commercialRegistryCertificate', 'notarialDeed']), 'DOCUMENTS');
    else {
      if (skipSave)
        await onSaveAndRedirect(payload, process);
      else
        await onSave(payload, process);
    }
    nextStep();
  }, [nextStep, onSave, onSaveAndRedirect]);
  
  return (
    <ProcedureContext.Provider
      value={{
        company,
        changeProcedureAndNext,
        nextStep,
        prevStep,
        processesInvolved,
        setProcessesInvolved,
        isFinal: step === processesInvolved.length + 1,
        step,
        setStep,
        onSave,
        onSubmit,
        isLoading: isLoading || isCreating || isUpdating || false,
        proposal,
        procedure,
        setProcedure,
        isDirectUpdate,
        isMepFixUpdate
      }}
    >
      {children}
    </ProcedureContext.Provider>
  );
}

function useProcedure() {
  const context = useContext(ProcedureContext);
  if (context === undefined) {
    throw new Error('useProcedure must be used within a ProcedureContext');
  }
  
  return context;
}

ProcedureProvider.propTypes = {
  children: PropTypes.any,
  company: PropTypes.any,
  data: PropTypes.any,
  companyId: PropTypes.string,
  proposal: PropTypes.any
};

export {ProcedureProvider, useProcedure};

